import React from "react";
import { useForm, useFieldArray, SubmitHandler } from "react-hook-form";
import { getSelectedMover, updateSelectedMover } from "@/store/mover/slice";
import { useUpdateMoverMutation } from "@/store/mover/apis";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
} from "@/components/ui/form";
import { useDispatch, useSelector } from "react-redux";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils"; // Make sure this utility is available in your project
import { PlusIcon, TrashIcon } from "lucide-react";

interface FeeInput {
  id?: number;
  name: string;
  amount: number | "";
  description: string | null | undefined;
  created_at: string | null | undefined;
  updated_at: string | null | undefined;
  _destroy?: boolean;
}

interface FeesFormValues {
  mover_fees: FeeInput[];
}

const Fees: React.FC = () => {
  const dispatch = useDispatch();
  const selectedMover = useSelector(getSelectedMover);
  const { toast } = useToast();
  const [updateMoverApi, { isLoading }] = useUpdateMoverMutation();

  const form = useForm<FeesFormValues>({
    defaultValues: {
      mover_fees:
        selectedMover?.mover_fees.map((fee) => ({
          id: fee.id,
          name: fee.name,
          amount: fee.amount ?? "",
          description: fee.description ?? "",
          created_at: fee.created_at ?? "",
          updated_at: fee.updated_at ?? "",
        })) || [],
    },
  });

  const { fields, prepend, remove } = useFieldArray({
    control: form.control,
    name: "mover_fees",
    keyName: "customId",
  });

  const onSubmit: SubmitHandler<FeesFormValues> = async (data) => {
    try {
      const updatedMover = await updateMoverApi({
        data: {
          mover_fees_attributes: data.mover_fees,
        },
      }).unwrap();

      dispatch(updateSelectedMover(updatedMover));

      form.reset({
        mover_fees: updatedMover.mover_fees.map((fee: FeeInput) => ({
          id: fee.id,
          name: fee.name,
          amount: fee.amount ?? "",
          description: fee.description ?? "",
          created_at: fee.created_at ?? "",
          updated_at: fee.updated_at ?? "",
        })),
      });

      toast({
        title: "Success",
        description: "Mover fees updated successfully.",
      });
    } catch (error) {
      console.error("Error updating mover fees:", error);
      toast({
        title: "Error",
        description: "Failed to update mover fees. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleRemoveFee = async (index: number) => {
    const feeToRemove = fields[index];

    // Only make API call if the fee exists in the backend (has an ID)
    if (feeToRemove.id) {
      try {
        await updateMoverApi({
          data: {
            mover_fees_attributes: [
              {
                id: feeToRemove.id,
                _destroy: true,
              },
            ],
          },
        }).unwrap();

        toast({
          title: "Success",
          description: "Fee removed successfully.",
        });
      } catch (error) {
        console.error("Error removing fee:", error);
        toast({
          title: "Error",
          description: "Failed to remove fee. Please try again.",
          variant: "destructive",
        });
        return; // Don't remove from form if API call fails
      }
    }

    // Remove from form state
    remove(index);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="w-full px-4 py-4 flex flex-col justify-start items-start gap-4">
          <div className="flex flex-row justify-start items-center w-full gap-4">
            <h2 className="text-lg font-bold">{"Settings > Fees"}</h2>
            <Button
              className="gap-1 flex flex-rowitems-center"
              size="sm"
              type="button"
              variant="secondary"
              onClick={() =>
                prepend({
                  name: "",
                  amount: 0,
                  description: "",
                  created_at: "",
                  updated_at: "",
                })
              }
            >
              <PlusIcon className="w-3 h-3" />
              Add New Fee
            </Button>
          </div>
          <div className="w-full grid grid-cols-12">
            <FormLabel className="col-span-3">Fee Name</FormLabel>
            <FormLabel className="col-span-4">
              Description
              <br />
              <span className="text-sm text-gray-500 font-normal">
                If you want to share an explanation for this fee with the
                customer, add it here.
              </span>
            </FormLabel>
            <FormLabel className="col-span-1">Amount</FormLabel>
            <FormLabel className="col-span-1"></FormLabel>
          </div>
          {fields.map((field, index) => (
            <div
              key={field.id}
              className="flex flex-row gap-4 items-center grid grid-cols-12"
            >
              <FormField
                control={form.control}
                name={`mover_fees.${index}.name`}
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={`mover_fees.${index}.description`}
                render={({ field }) => (
                  <FormItem className="col-span-4">
                    <FormControl>
                      <Input
                        value={field.value ?? ""}
                        onChange={(e) => field.onChange(e.target.value)}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={`mover_fees.${index}.amount`}
                render={({ field }) => (
                  <FormItem className="col-span-1">
                    <FormControl>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-900">
                          $
                        </span>
                        <Input
                          type="text"
                          value={field.value ? Math.floor(field.value) : ""}
                          onChange={(e) =>
                            field.onChange(parseInt(e.target.value))
                          }
                          className={cn("pl-6")}
                        />
                      </div>
                    </FormControl>
                  </FormItem>
                )}
              />
              <div className="col-span-1 flex items-center gap-1">
                <TrashIcon
                  className="w-4 h-4 cursor-pointer text-red-500"
                  onClick={() => handleRemoveFee(index)}
                />
                <span className="text-sm text-red-500">Remove</span>
                {/* <Button
                  type="button"
                  variant="destructive"
                  onClick={() => handleRemoveFee(index)}
                >
                  Remove Fee
                </Button> */}
              </div>
            </div>
          ))}

          <Button
            type="submit"
            disabled={isLoading}
            onClick={(e) => {
              e.preventDefault();
              form.handleSubmit(onSubmit)();
            }}
          >
            Save Changes
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default Fees;
